import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';

import { WEEKDAY_OPTIONS, WEEKDAY_SHORT_BY_NAME } from './weeklyAvailability';

export const WEEKEND_PERIOD_FRIDAY_TO_SUNDAY = 'friday_to_sunday';
export const WEEKEND_PERIOD_FRIDAY_TO_MONDAY = 'friday_to_monday';
export const WEEKEND_PERIOD_SATURDAY_TO_SUNDAY = 'saturday_to_sunday';
export const WEEKEND_PERIOD_CUSTOM = 'custom';

export const WEEKEND_AVAILABLE_EVERY = 'every_weekend';
export const WEEKEND_AVAILABLE_SPECIFIC = 'specific_weekends';

export const DEFAULT_WEEKEND_TIMEZONE = 'Europe/Oslo';
export const DEFAULT_WEEKEND_PICKUP_TIME = '16:00';
export const DEFAULT_WEEKEND_RETURN_TIME = '12:00';

const WEEKDAY_NAME_BY_INDEX = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

export const WEEKEND_BOOKING_PERIOD_OPTIONS = [
  {
    type: WEEKEND_PERIOD_FRIDAY_TO_SUNDAY,
    startDay: 'friday',
    endDay: 'sunday',
  },
  {
    type: WEEKEND_PERIOD_FRIDAY_TO_MONDAY,
    startDay: 'friday',
    endDay: 'monday',
  },
  {
    type: WEEKEND_PERIOD_SATURDAY_TO_SUNDAY,
    startDay: 'saturday',
    endDay: 'sunday',
  },
  {
    type: WEEKEND_PERIOD_CUSTOM,
    startDay: 'friday',
    endDay: 'monday',
  },
];

export const WEEKEND_AVAILABLE_MODE_OPTIONS = [WEEKEND_AVAILABLE_EVERY, WEEKEND_AVAILABLE_SPECIFIC];

const DAY_INDEX_BY_NAME = WEEKDAY_OPTIONS.reduce(
  (days, day, index) => ({
    ...days,
    [day]: index + 1 === 7 ? 0 : index + 1,
  }),
  {}
);

const normalizeWeekendDay = (day, fallback) => {
  const normalized = typeof day === 'string' ? day.toLowerCase() : null;
  return WEEKDAY_OPTIONS.includes(normalized) ? normalized : fallback;
};

export const getWeekendBookingPeriodOption = type => {
  return (
    WEEKEND_BOOKING_PERIOD_OPTIONS.find(option => option.type === type) ||
    WEEKEND_BOOKING_PERIOD_OPTIONS[0]
  );
};

const normalizeWeekendBookingPeriod = period => {
  const type = typeof period?.type === 'string' ? period.type : WEEKEND_PERIOD_FRIDAY_TO_SUNDAY;
  const option = getWeekendBookingPeriodOption(type);

  if (option.type === WEEKEND_PERIOD_CUSTOM) {
    return {
      type: WEEKEND_PERIOD_CUSTOM,
      startDay: normalizeWeekendDay(period?.startDay, option.startDay),
      endDay: normalizeWeekendDay(period?.endDay, option.endDay),
    };
  }

  return option;
};

const isValidDateString = value => moment(value, 'YYYY-MM-DD', true).isValid();

const normalizeDateRanges = dateRanges => {
  return Array.isArray(dateRanges)
    ? dateRanges
        .map(dateRange => {
          const startDate = dateRange?.startDate || '';
          const endDate = dateRange?.endDate || '';
          return startDate &&
            endDate &&
            isValidDateString(startDate) &&
            isValidDateString(endDate) &&
            endDate >= startDate
            ? { startDate, endDate }
            : null;
        })
        .filter(Boolean)
    : [];
};

const isValidTimeValue = value => {
  if (typeof value !== 'string' || !/^\d{2}:\d{2}$/.test(value)) {
    return false;
  }
  const [hours, minutes] = value.split(':').map(part => Number.parseInt(part, 10));
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
};

export const normalizeWeekendAvailability = (weekendAvailability, fallbackTimeZone) => {
  const availableWeekendMode = WEEKEND_AVAILABLE_MODE_OPTIONS.includes(
    weekendAvailability?.availableWeekendMode
  )
    ? weekendAvailability.availableWeekendMode
    : WEEKEND_AVAILABLE_EVERY;

  return {
    timezone: weekendAvailability?.timezone || fallbackTimeZone || DEFAULT_WEEKEND_TIMEZONE,
    weekendBookingPeriod: normalizeWeekendBookingPeriod(weekendAvailability?.weekendBookingPeriod),
    availableWeekendMode,
    specificAvailableWeekends: normalizeDateRanges(weekendAvailability?.specificAvailableWeekends),
    pickupTime: isValidTimeValue(weekendAvailability?.pickupTime)
      ? weekendAvailability.pickupTime
      : DEFAULT_WEEKEND_PICKUP_TIME,
    returnTime: isValidTimeValue(weekendAvailability?.returnTime)
      ? weekendAvailability.returnTime
      : DEFAULT_WEEKEND_RETURN_TIME,
    blockedWeekends: normalizeDateRanges(weekendAvailability?.blockedWeekends),
  };
};

export const createWeekendAvailabilityPlan = (timezone, weekendAvailability) => {
  const normalizedAvailability = normalizeWeekendAvailability(weekendAvailability, timezone);
  const hasSpecificAvailableWeekend =
    normalizedAvailability.availableWeekendMode !== WEEKEND_AVAILABLE_SPECIFIC ||
    normalizedAvailability.specificAvailableWeekends.length > 0;

  return {
    type: 'availability-plan/time',
    timezone: normalizedAvailability.timezone,
    entries: hasSpecificAvailableWeekend
      ? WEEKDAY_OPTIONS.map(day => ({
          dayOfWeek: WEEKDAY_SHORT_BY_NAME[day],
          seats: 1,
          startTime: '00:00',
          endTime: '00:00',
        }))
      : [],
  };
};

const bookingDayName = (date, timeZone) => {
  const dayIndex = moment(date)
    .tz(timeZone)
    .startOf('day')
    .day();
  return WEEKDAY_NAME_BY_INDEX[dayIndex];
};

const dateStringInTimeZone = (date, timeZone) => {
  return moment(date)
    .tz(timeZone)
    .startOf('day')
    .format('YYYY-MM-DD');
};

const dateRangeOverlapsInclusive = (dateRange, startDate, endDate, timeZone) => {
  const { startDate: blockedStartDate, endDate: blockedEndDate } = dateRange || {};

  if (!blockedStartDate || !blockedEndDate) {
    return false;
  }

  const bookingStart = moment(startDate)
    .tz(timeZone)
    .startOf('day');
  const bookingEnd = moment(endDate)
    .tz(timeZone)
    .startOf('day');
  const blockedStart = moment.tz(blockedStartDate, 'YYYY-MM-DD', timeZone).startOf('day');
  const blockedEnd = moment.tz(blockedEndDate, 'YYYY-MM-DD', timeZone).startOf('day');

  return !bookingStart.isAfter(blockedEnd) && !bookingEnd.isBefore(blockedStart);
};

const getBookingDateParts = (bookingDates, timeZone, endDateIsExclusive = true) => {
  const { startDate, endDate } = bookingDates || {};
  const hasDates = startDate instanceof Date && endDate instanceof Date;

  if (!hasDates) {
    return null;
  }

  const start = moment(startDate)
    .tz(timeZone)
    .startOf('day');
  const end = moment(endDate)
    .tz(timeZone)
    .startOf('day');
  const inclusiveEnd = endDateIsExclusive ? end.clone().subtract(1, 'day') : end;

  return {
    start,
    inclusiveEnd,
    startDate: start.toDate(),
    inclusiveEndDate: inclusiveEnd.toDate(),
    startDateString: start.format('YYYY-MM-DD'),
    endDateString: inclusiveEnd.format('YYYY-MM-DD'),
    startDay: WEEKDAY_NAME_BY_INDEX[start.day()],
    endDay: WEEKDAY_NAME_BY_INDEX[inclusiveEnd.day()],
    durationInDays: inclusiveEnd.diff(start, 'days'),
  };
};

const weekendPeriodDurationInDays = period => {
  const startIndex = DAY_INDEX_BY_NAME[period.startDay];
  const endIndex = DAY_INDEX_BY_NAME[period.endDay];
  const duration = (endIndex - startIndex + 7) % 7;
  return duration === 0 ? 7 : duration;
};

const weekendRangeMatches = (dateRange, startDateString, endDateString) =>
  dateRange?.startDate === startDateString && dateRange?.endDate === endDateString;

const isWeekendStartDateAvailable = (startDate, normalizedAvailability, timeZone) => {
  const startDateString = dateStringInTimeZone(startDate, timeZone);
  const startDay = bookingDayName(startDate, timeZone);
  const period = normalizedAvailability.weekendBookingPeriod;

  if (startDay !== period.startDay) {
    return false;
  }

  const isBlocked = normalizedAvailability.blockedWeekends.some(blockedWeekend =>
    dateRangeOverlapsInclusive(blockedWeekend, startDate, startDate, timeZone)
  );

  if (isBlocked) {
    return false;
  }

  return normalizedAvailability.availableWeekendMode === WEEKEND_AVAILABLE_SPECIFIC
    ? normalizedAvailability.specificAvailableWeekends.some(
        dateRange => dateRange?.startDate === startDateString
      )
    : true;
};

export const validateWeekendBookingDates = (
  bookingDates,
  weekendAvailability,
  timeZone,
  options = {}
) => {
  const { endDateIsExclusive = true } = options;

  if (!weekendAvailability) {
    return null;
  }

  const normalizedAvailability = normalizeWeekendAvailability(weekendAvailability, timeZone);
  const bookingTimeZone = normalizedAvailability.timezone || timeZone || DEFAULT_WEEKEND_TIMEZONE;
  const dateParts = getBookingDateParts(bookingDates, bookingTimeZone, endDateIsExclusive);

  if (!dateParts) {
    return null;
  }

  const period = normalizedAvailability.weekendBookingPeriod;
  const expectedDurationInDays = weekendPeriodDurationInDays(period);

  if (dateParts.startDay !== period.startDay) {
    return { code: 'startDay', allowedStartDay: period.startDay };
  }

  if (dateParts.endDay !== period.endDay || dateParts.durationInDays !== expectedDurationInDays) {
    return {
      code: 'period',
      startDay: period.startDay,
      endDay: period.endDay,
    };
  }

  if (
    normalizedAvailability.availableWeekendMode === WEEKEND_AVAILABLE_SPECIFIC &&
    !normalizedAvailability.specificAvailableWeekends.some(dateRange =>
      weekendRangeMatches(dateRange, dateParts.startDateString, dateParts.endDateString)
    )
  ) {
    return { code: 'specific' };
  }

  const overlapsBlockedWeekend = normalizedAvailability.blockedWeekends.some(blockedWeekend =>
    dateRangeOverlapsInclusive(
      blockedWeekend,
      dateParts.startDate,
      dateParts.inclusiveEndDate,
      bookingTimeZone
    )
  );

  return overlapsBlockedWeekend ? { code: 'blocked' } : null;
};

export const isWeekendBookingEndpointAvailable = (
  day,
  startDate,
  endDate,
  weekendAvailability,
  timeZone
) => {
  const normalizedAvailability = normalizeWeekendAvailability(weekendAvailability, timeZone);
  const bookingTimeZone = normalizedAvailability.timezone || timeZone || DEFAULT_WEEKEND_TIMEZONE;
  const dayStart = moment(day)
    .tz(bookingTimeZone)
    .startOf('day')
    .toDate();

  if (!startDate || endDate) {
    return isWeekendStartDateAvailable(dayStart, normalizedAvailability, bookingTimeZone);
  }

  const exclusiveEndDate = moment(dayStart)
    .tz(bookingTimeZone)
    .startOf('day')
    .add(1, 'day')
    .toDate();

  return !validateWeekendBookingDates(
    { startDate, endDate: exclusiveEndDate },
    normalizedAvailability,
    bookingTimeZone
  );
};

const applyTimeToDate = (date, timeValue, timeZone) => {
  const [hours, minutes] = timeValue.split(':').map(part => Number.parseInt(part, 10));
  return moment(date)
    .tz(timeZone)
    .startOf('day')
    .hour(hours)
    .minute(minutes)
    .second(0)
    .millisecond(0)
    .toDate();
};

export const resolveWeekendBookingDates = (
  bookingDates,
  weekendAvailability,
  timeZone,
  options = {}
) => {
  const { endDateIsExclusive = true } = options;

  if (!weekendAvailability) {
    return bookingDates;
  }

  const normalizedAvailability = normalizeWeekendAvailability(weekendAvailability, timeZone);
  const bookingTimeZone = normalizedAvailability.timezone || timeZone || DEFAULT_WEEKEND_TIMEZONE;
  const dateParts = getBookingDateParts(bookingDates, bookingTimeZone, endDateIsExclusive);

  if (!dateParts) {
    return bookingDates;
  }

  return {
    startDate: applyTimeToDate(
      dateParts.startDate,
      normalizedAvailability.pickupTime,
      bookingTimeZone
    ),
    endDate: applyTimeToDate(
      dateParts.inclusiveEndDate,
      normalizedAvailability.returnTime,
      bookingTimeZone
    ),
  };
};
